import React, { useCallback, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import RichSelectField from './formfields/RichSelectField';
import useOrderApi from '../hooks/useOrderApi';
import omit from 'lodash/omit';
import { useFormStore } from '../stores/formData';

const PackagingAndProtection = ({ 
  legIndex,
  onInsuranceChange,
  updatingCart = false
}) => {
  // State for tracking packaging and insurance selections
  const [selectedInsurance, setSelectedInsurance] = useState({});
  const [productLines, setProductLines] = useState([]);
  const [hasSetDefaultInsurance, setHasSetDefaultInsurance] = useState(false);
  const isMountedRef = useRef(true);
  
  const helpers = useFormStore((state) => state.helpers);
  const actions = useFormStore((state) => state.actions);
  const brandId = useFormStore((state) => state.formData.brandId);
  const currentLeg = useFormStore((state) => state.formData.legs[legIndex]);

  const { getProducts } = useOrderApi();

  // Single cleanup effect to track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initialize selections when products change
  useEffect(() => {
    if (productLines.length > 0) return;

    const buildProductLines = async () => {
      const leg = helpers.getLeg(legIndex);
      const rates = leg?.pricing?.rates || [];
      const newProductLines = [];
      
      for (const rate of rates) {
        if (!isMountedRef.current) return;
        
        const productLineId = rate.id;
        
        const fromCountry = leg?.fromAddress?.country_code || 
          leg?.fromAddress?.cruisePort?.country_code || 
          leg?.fromAddress?.cruisePort?.country ||
          'US';

        const toCountry = leg?.toAddress?.country_code || 
          leg?.toAddress?.cruisePort?.country_code || 
          leg?.toAddress?.cruisePort?.country || 'US';

        const domestic = fromCountry === 'US' && toCountry === 'US';

        const uscan = ['US','CA'];
        // if US or CA specify insurance country otherwise use US
        // otherwise the international insurance won't load
        const insurance_country_code = 
          (uscan.includes(fromCountry) && uscan.includes(toCountry)) ? fromCountry : 'US';
          
        try {
          const response = await getProducts(
            brandId, {
            product_type: 'insurance',
            public: true,
            active: true,
            domestic: domestic,
            international: !domestic,
            country_code: insurance_country_code,
            product_line_id: productLineId
          });
          
          if (response && isMountedRef.current) {
            const { products } = response;
            if (products) {
              // filter out the bad icon field
              const filteredInsurance = products.map((p) => ({
                ...omit(p, ['icon', 'label'])
              }));
              const defaultCoverage = filteredInsurance.find(ins => ins.default_coverage === true) || {};

              const productLine = {
                ...rate,
                insurance: filteredInsurance, 
                defaultInsurance: defaultCoverage
              };
              newProductLines.push(productLine);
            }
          }
        } catch (error) {
          console.error('Error fetching product line:', error);
        }
      }
      
      if (isMountedRef.current) {
        setProductLines(newProductLines);
      }
    };
    
    buildProductLines();
  }, [getProducts, brandId, legIndex, helpers.getLeg]);

  // Auto-select default insurance when product lines are loaded
  useEffect(() => {
    if (productLines.length === 0 || hasSetDefaultInsurance) return;

    const setDefaults = async () => {
      // Allow any micro-tasks to settle
      await Promise.resolve();
      
      if (!isMountedRef.current) return;

      const leg = helpers.getLeg(legIndex);
      if (!leg?.selectedProducts) return;

      const updates = {};
      let hasUpdates = false;
      
      leg.selectedProducts.forEach(selectedProduct => {
        // Skip if insurance is already selected
        if (selectedProduct.insurance?.id) return;
        
        // Find the product line for this product
        let productLine = null;
        for (const line of productLines) {
          const productDetails = line.products?.find(p => p.product_id === selectedProduct.product_id);
          if (productDetails) {
            productLine = line;
            break;
          }
        }
        
        if (productLine?.defaultInsurance) {
          const defaultInsurance = productLine.defaultInsurance;
          
          // Update the product with default insurance
          actions.leg.updateProductField(
            legIndex,
            selectedProduct.product_id,
            'insurance',
            defaultInsurance
          );
            
          // Update local state
          updates[`${legIndex}:${selectedProduct.product_id}`] = defaultInsurance.id;
          hasUpdates = true;
        }
      });

      if (isMountedRef.current && hasUpdates) {
        setSelectedInsurance(prev => ({ ...prev, ...updates }));
        console.log('Default insurance selections applied:', updates);
        setHasSetDefaultInsurance(true);
        onInsuranceChange?.();
      }
    };

    setDefaults();
  }, [
    productLines,
    hasSetDefaultInsurance,
    helpers.getLeg,
    legIndex,
    actions.leg.updateProductField,
    onInsuranceChange
  ]);

  const formatPrice = (cents) => {
    return (cents / 100).toFixed(2);
  };

  const handleInsuranceChange = useCallback((productId, insurance) => {
    setSelectedInsurance(prev => ({
      ...prev,
      [`${legIndex}:${productId}`]: insurance.id
    }));
    // save the selected insurance on the leg
    actions.leg.updateProductField(
      legIndex,
      productId,
      'insurance',
      insurance
    );
    console.log('Default insurance selections applied:', selectedInsurance);
        
    onInsuranceChange?.();
  }, [legIndex, actions.leg.updateProductField, onInsuranceChange]);

  return (
    <div className="w-full flex-col md:flex-row bg-white">
      <div className="flex flex-col md:flex-row gap-2">
        {currentLeg?.selectedProducts?.map(selectedProduct => {
          // Find the product details and product line
          let productDetails = null;
          let productLine = null;
          for (const line of productLines) {
            productDetails = line.products?.find(p => p.product_id === selectedProduct.product_id);
            if (productDetails) {
              productLine = line;
              break;
            }
          }

          const insuranceOptions = productLine?.insurance || [];
          const defaultInsurance = productLine?.defaultInsurance || null;
          return (
            <div key={selectedProduct.product_id} 
                 className="w-full max-w-[300px] px-2 rounded-xl space-y-3 last:mb-0">
              {/* Product Header */}
              <div className="flex flex-col md:gap-2">
                <div className="text-gray-600 text-sm font-medium text-left">
                  {productDetails?.display_name || selectedProduct.name} {productDetails?.product_line} ({selectedProduct.quantity})
                </div>
                
                {/* Insurance Dropdown */}
                {insuranceOptions?.length > 0 && (
                  <div className="relative">
                    <label className="text-xs">Coverage amount</label>
                    <RichSelectField
                      id={`insurance-${productLine.id}-${selectedProduct.product_id}`}
                      value={selectedProduct?.insurance?.id || defaultInsurance?.id || null} 
                      options={insuranceOptions}
                      useCustomDropdown={true}
                      disabled={updatingCart}
                      valueKey="id"
                      placeholder="Select insurance"
                      formatValue={(option) => ({
                          key: option?.id,
                          icon: option?.icon?.web || null,
                          label: `$${option.coverage_amount} ($${formatPrice(option.price_cents)})`,
                          value: option?.id
                      })}
                      onChange={(event) => {
                        const { value } = event.target;
                        const selectedInsurance = insuranceOptions.find(ins => ins.id === value);
                        handleInsuranceChange(selectedProduct.product_id, selectedInsurance);
                      }}
                      className="text-xs"
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

PackagingAndProtection.propTypes = {
  legIndex: PropTypes.number.isRequired,
  onInsuranceChange: PropTypes.func,
  updatingCart: PropTypes.bool
}

export default PackagingAndProtection;